import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import {
  BarChart3,
  Check,
  Circle,
  ClipboardCheck,
  FileSignature,
  FolderOpen,
  HeartPulse,
  Hospital,
  Hourglass,
  Info,
  Mail,
  MapPin,
  Minus,
  Scale,
  ScanSearch,
  Shield,
  Send,
  ShieldCheck,
  Upload,
  UserCog,
  X,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import AddressAdder from './AddressAdder';
import { isCarriedSa } from './clientOnboardingUtils';
import DatePickerField from '../onboardingSupport/DatePickerField';

// The checklist in the Client Onboarding slide-over (Nic 2026-09-28): the
// Case Readiness steps, questions and green-check rules exactly as the Case
// Readiness panel had them (the server owns the rules and sends each step's
// status), in that panel's look: a gradient progress bar, a Ready / Not
// ready verdict with the blockers, and one accordion card per step with
// Yes / No answers, follow-up questions on a connector line, and who
// answered when. A "No" that is fine reads amber; a "No" that blocks reads
// rose.

export const STEP_ICON = {
  signed_agreement: FileSignature,
  sa_confirmed: ScanSearch,
  va_gov: ShieldCheck,
  // Verify POA (Nic 2026-10-27).
  poa: Scale,
  itf: ClipboardCheck,
  dd214: Shield,
  rated: BarChart3,
  private_records: Hospital,
  service_records: FolderOpen,
  blue_button: HeartPulse,
  decision_letters: Mail,
  address: MapPin,
  // Claims Manager preferences (Nic 2026-11-02).
  cm_preferences: UserCog,
};

const PILL = {
  pass: {
    label: 'Complete',
    cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
  fail: {
    label: 'Incomplete',
    cls: 'bg-rose-500/10 text-rose-600 dark:text-rose-300',
    dot: 'bg-rose-500',
  },
  pending: {
    label: 'Pending',
    cls: 'bg-slate-100 text-slate-400 dark:!bg-white/5 dark:text-slate-500',
    dot: 'bg-slate-300 dark:bg-slate-600',
  },
  // Round 3 (Nic 2026-10-10): where an open document step stands.
  todo: {
    label: 'To do',
    cls: 'bg-slate-100 text-slate-500 dark:!bg-white/5 dark:text-slate-400',
    dot: 'bg-slate-300 dark:bg-slate-600',
  },
  client: {
    label: 'Client is getting it',
    cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
  requested: {
    label: 'Requested',
    cls: 'bg-violet-500/10 text-violet-700 dark:text-violet-300',
    dot: 'bg-violet-500',
  },
  na: {
    label: "Doesn't apply",
    cls: 'bg-slate-100 text-slate-500 dark:!bg-white/5 dark:text-slate-400',
    dot: 'bg-slate-400',
  },
  // VA.gov only (Nic 2026-10-05): the client's step, or said set up and
  // waiting to be seen on the call.
  vaWaiting: {
    label: 'Their step',
    cls: 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
    dot: 'bg-sky-500',
  },
  vaReady: {
    label: 'Check on call',
    cls: 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
    dot: 'bg-sky-500 animate-pulse motion-reduce:animate-none',
  },
  // Verify POA (Nic 2026-10-27): they tapped I've done it in their portal
  // and their specialist hasn't confirmed it yet.
  poaDone: {
    label: "They say it's done",
    cls: 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
    dot: 'bg-sky-500 animate-pulse motion-reduce:animate-none',
  },
};

// The VA.gov step's pill follows its stage, not just pass / fail.
export const pillStatusOf = (step, vaGov) => {
  // A document step says where it stands (round 3): Doesn't apply, or an
  // open step's To do / Client is getting it / Requested.
  if (step.state) {
    if (step.state === 'na') return 'na';
    if (step.status !== 'pass') return ['client', 'requested'].includes(step.state) ? step.state : 'todo';
    return step.status;
  }
  if (step.id !== 'va_gov' || step.status === 'pass' || !vaGov?.stage || vaGov.checkedNo) {
    return step.status;
  }
  return vaGov.stage === 'ready' ? 'vaReady' : 'vaWaiting';
};

export const StatusPill = ({ status }) => {
  const p = PILL[status] || PILL.pending;
  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide', p.cls)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', p.dot)} />
      {p.label}
    </span>
  );
};

// Shared with the Verify POA step (PoaStep.js, Nic 2026-10-27).
// goodIs (Nic 2026-10-27): which answer is the good news, green. 'yes' for
// most steps; 'no' for Verify POA ("Has a POA to remove?"), where Yes is
// the one that means work, amber.
export const YesNo = ({ value, noIsOk, disabled, onChange, goodIs = 'yes' }) => (
  <div className="flex gap-2">
    {['yes', 'no'].map((opt) => {
      const on = value === opt;
      const isYes = opt === 'yes';
      const good = opt === goodIs;
      const tone = good
        ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 shadow-[0_0_12px_rgba(34,197,94,0.15)] dark:text-emerald-300'
        : noIsOk || goodIs === 'no'
          ? 'border-amber-500 bg-amber-500/10 text-amber-700 shadow-[0_0_12px_rgba(245,158,11,0.15)] dark:text-amber-300'
          : 'border-rose-500 bg-rose-500/10 text-rose-600 shadow-[0_0_12px_rgba(244,63,94,0.15)] dark:text-rose-300';
      const hover = good
        ? 'hover:border-emerald-500'
        : noIsOk || goodIs === 'no'
          ? 'hover:border-amber-500'
          : 'hover:border-rose-500';
      return (
        <button
          key={opt}
          type="button"
          disabled={disabled}
          onClick={() => !on && onChange(opt)}
          className={cn(
            'inline-flex h-8 items-center gap-1 whitespace-nowrap rounded-lg border-2 px-3.5 text-[13px] outline-none transition-[transform,box-shadow] duration-150 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60',
            on
              ? cn('scale-[1.03] font-semibold', tone)
              : cn('border-slate-200 bg-white font-normal text-slate-400 dark:border-white/10 dark:!bg-white/5 dark:text-slate-400', !disabled && hover),
          )}
        >
          {isYes ? <Check size={13} /> : <X size={13} />} {isYes ? 'Yes' : 'No'}
        </button>
      );
    })}
  </div>
);

// One tap sets where a document step stands (Nic 2026-10-10). Each step
// sends its own options (a service record always applies, an ITF is never
// "requested").
const STATE_TONE = {
  todo: 'border-slate-400 bg-slate-500/10 text-slate-700 dark:text-slate-200',
  got: 'border-emerald-500 bg-emerald-500/10 text-emerald-700 shadow-[0_0_12px_rgba(34,197,94,0.15)] dark:text-emerald-300',
  client: 'border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  requested: 'border-violet-500 bg-violet-500/10 text-violet-700 dark:text-violet-300',
  na: 'border-slate-400 bg-slate-500/10 text-slate-600 dark:text-slate-300',
};
const STATE_ICON = { todo: Circle, got: Check, client: Hourglass, requested: Send, na: Minus };
const StateButtons = ({ options = [], value, disabled, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => {
      const on = (value || 'todo') === o.key;
      const Icon = STATE_ICON[o.key];
      return (
        <button
          key={o.key}
          type="button"
          disabled={disabled}
          onClick={() => !on && onChange(o.key)}
          aria-pressed={on}
          className={cn(
            'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg border-2 px-3 text-[13px] outline-none transition-[transform,box-shadow] duration-150 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60',
            on
              ? cn('font-semibold', STATE_TONE[o.key])
              : 'border-slate-200 bg-white font-normal text-slate-500 hover:border-slate-400 dark:border-white/10 dark:!bg-white/5 dark:text-slate-400',
          )}
        >
          {Icon ? <Icon size={13} strokeWidth={on ? 2.5 : 2} className={on ? undefined : 'opacity-60'} /> : null}
          {o.label}
        </button>
      );
    })}
  </div>
);

// The app's own calendar (Nic 2026-10-26), not the browser's date box. A
// checklist date is never in the future (an Intent to File already filed).
const DateField = ({ value, disabled, onChange, label }) => (
  <DatePickerField value={value || null} disabled={disabled} onChange={onChange} label={label} />
);

const Attribution = ({ meta }) =>
  meta?.name && meta?.at ? (
    <div className="mt-1.5 text-[11px] italic text-slate-400 dark:text-slate-500">
      {meta.name} · {moment(meta.at).fromNow()}
    </div>
  ) : null;

// The VA.gov step's own panel lives in VaGovStep.js (Nic 2026-10-25: one
// Confirm and a short history; the old three-column trail and Yes / No are
// gone).

// Readiness, as one edge-to-edge strip under the slide-over's header (Nic
// 2026-10-05: not another tile on the stack). Status on the left, progress
// on the right, what is still open as small chips underneath. Tinted from
// the left so it reads as a banner, not a card.
const MAX_CHIPS = 4;
export const ReadinessBanner = ({ steps }) => {
  const done = steps.filter((s) => s.status === 'pass').length;
  const total = steps.length || 1;
  const ready = done === steps.length;
  const open = steps.filter((s) => s.status !== 'pass');
  return (
    <div
      className={cn(
        'border-b border-slate-200/80 px-5 py-3 dark:border-white/10',
        // Solid, not faded (Nic 2026-10-05): red until ready, then green.
        ready
          ? 'bg-emerald-50 dark:!bg-emerald-500/15'
          : 'bg-rose-50 dark:!bg-rose-500/15',
      )}
    >
      <div className="flex items-center gap-3">
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white',
            ready ? 'bg-emerald-500' : 'bg-rose-500',
          )}
        >
          {ready ? <Check size={15} strokeWidth={3} /> : <ClipboardCheck size={14} />}
        </span>
        <div
          className={cn(
            'min-w-0 flex-1 truncate text-sm font-bold',
            ready ? 'text-emerald-700 dark:text-emerald-200' : 'text-rose-700 dark:text-rose-200',
          )}
        >
          {ready ? 'Ready for a Claims Manager' : 'Not ready for a Claims Manager'}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div
            className={cn(
              'h-1.5 w-20 overflow-hidden rounded-full',
              ready ? 'bg-emerald-200/70 dark:!bg-white/10' : 'bg-rose-200/70 dark:!bg-white/10',
            )}
          >
            <div
              className={cn(
                'h-full rounded-full transition-[width] duration-500 ease-out',
                ready ? 'bg-emerald-500' : 'bg-rose-500',
              )}
              style={{ width: `${(done / total) * 100}%` }}
            />
          </div>
          <span
            className={cn(
              'text-xs font-semibold tabular-nums',
              ready ? 'text-emerald-700 dark:text-emerald-200' : 'text-rose-700 dark:text-rose-200',
            )}
          >
            {done}/{steps.length}
          </span>
        </div>
      </div>
      {!ready && open.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5 pl-10">
          {open.slice(0, MAX_CHIPS).map((s) => (
            <span
              key={s.id}
              className={cn(
                'whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium',
                s.status === 'fail'
                  ? 'bg-rose-500 text-white'
                  : 'bg-white text-rose-700 ring-1 ring-rose-200 dark:!bg-rose-500/10 dark:text-rose-200 dark:ring-rose-500/30',
              )}
            >
              {s.title}
            </span>
          ))}
          {open.length > MAX_CHIPS ? (
            <span className="whitespace-nowrap px-1 py-0.5 text-[11px] font-medium text-slate-400">
              +{open.length - MAX_CHIPS} more
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const visibleFields = (step, answers) =>
  step.fields.filter((f) => !f.showIf || answers[f.showIf.field] === f.showIf.value);

// One step's work (2026-10-09: shared with the Client Workspace's
// two-pane checklist): the hint, the VA.gov trail, uploads, the questions
// with who answered when.
export const StepDetail = ({
  step,
  answers,
  answerMeta,
  vaGov,
  signedAt,
  locked,
  onAnswer,
  address,
  savingAddress,
  onSaveAddress,
}) => {
  const fields = visibleFields(step, answers);
  return (
    <>
    {/* What the step is after, in a line (Nic 2026-10-11: "more clear
        what each section is and what's needed"). */}
    {step.need ? (
      <p className="mb-0 mt-1.5 text-[13px] leading-relaxed text-slate-600 dark:text-slate-300">{step.need}</p>
    ) : null}
    {step.uploads ? (
      <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700 dark:!bg-sky-500/10 dark:text-sky-300">
        <Upload size={12} /> {step.uploads} file{step.uploads === 1 ? '' : 's'} on this step
      </div>
    ) : null}
    {isCarriedSa(step, answerMeta) ? (
      // Confirm service agreement is for new clients signed in the app (Nic
      // 2026-10-27): a carried-over client has it checked by default.
      <div className="mt-3 flex items-center gap-2 rounded-lg bg-emerald-500/[0.07] px-3 py-2.5 text-[13px] font-semibold italic text-slate-700 dark:text-slate-200">
        <Check size={15} className="shrink-0 text-emerald-600 dark:text-emerald-300" />
        Checked by default. Only available to new clients.
      </div>
    ) : step.id === 'signed_agreement' ? (
      // Signing is what brings a client here, so this one is
      // always done.
      <div className="mt-3 flex items-center gap-2 rounded-lg bg-emerald-500/[0.07] px-3 py-2.5 text-[13px] font-semibold text-slate-700 dark:text-slate-200">
        <Check size={15} className="shrink-0 text-emerald-600 dark:text-emerald-300" />
        Signed with the Sales Team
        {signedAt ? (
          <span className="font-normal text-slate-500 dark:text-slate-400">
            {' '}
            · {moment(signedAt).format('MMM D, YYYY')}
          </span>
        ) : null}
      </div>
    ) : step.auto ? (
      // The address checks itself: show it, or add it here.
      address?.verified ? (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-emerald-500/[0.07] px-3 py-2.5 text-[13px] text-slate-700 dark:text-slate-200">
          <Check size={15} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-300" />
          <div>
            <div className="font-semibold">
              {address.line1}
              {address.line2 ? `, ${address.line2}` : ''}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {address.city}, {address.state} {address.zip} · Verified
            </div>
          </div>
        </div>
      ) : (
        <>
          <p className="mb-0 mt-3 text-[13px] text-slate-600 dark:text-slate-300">
            {address?.line1
              ? `On file but not verified: ${address.line1}. Pick it from the suggestions to verify it.`
              : 'No address on file yet. Add it here.'}
          </p>
          <AddressAdder disabled={locked} saving={savingAddress} onSave={onSaveAddress} />
        </>
      )
    ) : null}
    {/* Confirm service agreement has no questions: its Workspace panel
        (SaCheckPanel) does the work. */}
    {(step.auto || step.staffOnly || isCarriedSa(step, answerMeta) ? [] : fields).map((f, idx) => {
      const isChild = !!f.showIf;
      const next = fields[idx + 1];
      const lastChild = isChild && (!next || !next.showIf);
      return (
        <div
          key={f.id}
          className={cn(
            'relative mt-3.5',
            isChild && 'ml-4 pl-5',
          )}
        >
          {isChild ? (
            <span
              aria-hidden
              className={cn(
                'absolute left-0 w-3 border-l-2 border-slate-300/80 dark:border-white/15',
                lastChild
                  ? '-top-3.5 h-6 rounded-bl-md border-b-2'
                  : '-top-3.5 bottom-0',
              )}
            />
          ) : null}
          {isChild && !lastChild ? (
            <span
              aria-hidden
              className="absolute left-0 top-2.5 w-3 border-t-2 border-slate-300/80 dark:border-white/15"
            />
          ) : null}
          {f.type === 'state' ? null : (
            <p className="mb-2 text-[13px] font-medium text-slate-600 dark:text-slate-300">{f.label}</p>
          )}
          {f.type === 'state' ? (
            <StateButtons
              options={f.options}
              value={answers[f.id]}
              disabled={locked}
              onChange={(v) => onAnswer(f.id, v)}
            />
          ) : f.type === 'yesno' ? (
            <YesNo
              value={answers[f.id]}
              noIsOk={f.noIsOk}
              disabled={locked}
              onChange={(v) => onAnswer(f.id, v)}
            />
          ) : (
            <DateField
              value={answers[f.id]}
              label={f.label}
              disabled={locked}
              onChange={(v) => onAnswer(f.id, v)}
            />
          )}
          <Attribution meta={answerMeta[f.id]} />
        </div>
      );
    })}
    {/* How to get it (Objectives PDF 2026-10-01): the phone
        number, the archives site, VA.gov's own setup steps. */}
    {step.hint ? (
      <div className="mt-3 flex items-start gap-2 rounded-lg bg-indigo-50/70 px-3 py-2 text-xs leading-relaxed text-indigo-900 dark:!bg-indigo-500/10 dark:text-indigo-200">
        <Info size={13} className="mt-0.5 shrink-0 opacity-70" />
        <span className="min-w-0">
          <span className="font-semibold">How to get it: </span>
          {step.hint}
        </span>
      </div>
    ) : null}
    </>
  );
};

const ReadinessChecklist = ({
  steps,
  answers,
  answerMeta,
  vaGov,
  signedAt,
  locked,
  onAnswer,
  address,
  savingAddress,
  onSaveAddress,
}) => {
  // Open the first step that still needs work.
  const firstOpen = steps.findIndex((s) => s.status !== 'pass');
  const [active, setActive] = useState(firstOpen);
  useEffect(() => {
    setActive(firstOpen);
    // Only on the client changing (the steps list identity), never on each
    // answer: the accordion must not jump while they work a step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps.length]);

  // The readiness summary lives in ReadinessBanner (the slide-over's strip
  // under its header, Nic 2026-10-05); this is just the steps.
  return (
    <section>
      <div className="space-y-1.5">
        {steps.map((step, i) => {
          const isActive = active === i;
          const Icon = STEP_ICON[step.id] || ClipboardCheck;
          return (
            <div
              key={step.id}
              className={cn(
                'overflow-hidden rounded-xl border-[1px] bg-white transition-[border-color,box-shadow] dark:!bg-surface-card',
                isActive
                  ? 'border-indigo-400/40 shadow-[0_0_16px_rgba(99,102,241,0.08)] dark:border-indigo-400/30'
                  : 'border-slate-200 shadow-[0_1px_2px_rgba(0,0,0,0.04)] dark:border-white/10',
              )}
            >
              <button
                type="button"
                onClick={() => setActive(isActive ? -1 : i)}
                className={cn(
                  'flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left outline-none focus:outline-none',
                  !isActive && 'hover:bg-slate-50 dark:hover:!bg-white/[0.03]',
                )}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <span
                    className={cn(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold',
                      isActive
                        ? 'bg-gradient-to-br from-indigo-500 to-violet-500 text-white'
                        : step.status === 'pass'
                          ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300'
                          : step.status === 'fail'
                            ? 'bg-rose-500/15 text-rose-600 dark:text-rose-300'
                            : 'bg-slate-100 text-slate-400 dark:!bg-white/5',
                    )}
                  >
                    {!isActive && step.status === 'pass' ? (
                      <Check size={14} />
                    ) : !isActive && step.status === 'fail' ? (
                      <X size={14} />
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span
                    className={cn(
                      'flex min-w-0 items-center gap-2 text-[13px] font-medium',
                      isActive ? 'text-slate-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300',
                    )}
                  >
                    <Icon size={15} className="shrink-0 opacity-60" />
                    <span className={cn('truncate', isCarriedSa(step, answerMeta) && 'italic line-through opacity-70')}>{step.title}</span>
                  </span>
                </span>
                <StatusPill status={pillStatusOf(step, vaGov)} />
              </button>

              {isActive ? (
                <div className="border-t border-slate-100 px-4 pb-4 pt-1.5 dark:border-white/10">
                  <StepDetail
                    step={step}
                    answers={answers}
                    answerMeta={answerMeta}
                    vaGov={vaGov}
                    signedAt={signedAt}
                    locked={locked}
                    onAnswer={onAnswer}
                    address={address}
                    savingAddress={savingAddress}
                    onSaveAddress={onSaveAddress}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ReadinessChecklist;
